/** Meets (DESIGN §27.3): the calendar's days, what is on each, and what a meet must be to be saved. */
import { addDays, dayLong, dayString, isDay, mondayOf, parseDay } from "./days.js";
import type { Meet } from "./types.js";

export const MEET_TEXT_MAX = 80;

export function monthOf(day: string): string {
  return day.slice(0, 8) + "01";
}

export function addMonths(month: string, n: number): string {
  const d = parseDay(monthOf(month));
  d.setMonth(d.getMonth() + n);
  return dayString(d);
}

export function monthTitle(month: string): string {
  return parseDay(month).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/** The month's weeks, Monday first, each seven days; the first and last carry days of the months beside it. */
export function monthGrid(month: string): string[][] {
  const first = monthOf(month);
  const last = addDays(addMonths(first, 1), -1);
  const weeks: string[][] = [];
  for (let monday = mondayOf(first); monday <= last; monday = addDays(monday, 7)) weeks.push(Array.from({ length: 7 }, (_, i) => addDays(monday, i)));
  return weeks;
}

/** A meet's last day: its end when it has one after its start, else its date. */
export function lastDay(m: Pick<Meet, "date" | "endDate">): string {
  return m.endDate && m.endDate > m.date ? m.endDate : m.date;
}

export function isOn(m: Pick<Meet, "date" | "endDate">, day: string): boolean {
  return day >= m.date && day <= lastDay(m);
}

export const byDate = (a: Meet, b: Meet) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

export function meetsOn(meets: readonly Meet[], day: string): Meet[] {
  return meets.filter((m) => isOn(m, day)).sort(byDate);
}

/** The meets still to come, a meet under way included, soonest first. */
export function comingUp(meets: readonly Meet[], today: string): Meet[] {
  return meets.filter((m) => lastDay(m) >= today).sort(byDate);
}

/** Whole days from today to the meet's first day; 0 while it is on. */
export function daysAway(m: Pick<Meet, "date" | "endDate">, today: string): number {
  if (isOn(m, today)) return 0;
  return Math.round((parseDay(m.date).getTime() - parseDay(today).getTime()) / 86_400_000);
}

const short = (s: string, year: boolean) => parseDay(s).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", ...(year ? { year: "numeric" } : {}) });

/** "Sat, Oct 24", or "Sat, Oct 24 – Sun, Oct 25"; the year only when it isn't this one. */
export function meetWhen(m: Pick<Meet, "date" | "endDate">, thisYear = new Date().getFullYear()): string {
  const end = lastDay(m);
  const year = parseDay(m.date).getFullYear() !== thisYear || parseDay(end).getFullYear() !== thisYear;
  return end === m.date ? short(m.date, year) : short(m.date, year) + " – " + short(end, year);
}

/** The same, said in full for VoiceOver. */
export function meetWhenLong(m: Pick<Meet, "date" | "endDate">): string {
  const end = lastDay(m);
  return end === m.date ? dayLong(m.date) : dayLong(m.date) + " to " + dayLong(end);
}

export const goingText = (n: number) => (n === 1 ? "1 going" : n + " going");

export function inDays(n: number): string {
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n < 0) return n === -1 ? "yesterday" : -n + " days ago";
  return "in " + n + " days";
}

/** Who is going, in the order the athletes are given; ids no longer on the team are left out. */
export function goingNames(m: Pick<Meet, "people">, athletes: readonly { id: string; name: string }[]): string[] {
  const going = new Set(m.people);
  return athletes.filter((a) => going.has(a.id)).map((a) => a.name);
}

/** Who is going as one line (§27.3.1): "Dana, Robin, Sam"; past `max`, the first `max` and "+3". */
export function namesLine(names: readonly string[], max = 6): string {
  if (names.length <= max) return names.join(", ");
  return names.slice(0, max).join(", ") + " +" + (names.length - max);
}

/** The meets this athlete is going to, soonest first, each once however many reads it came from. */
export function meetsFor(athleteID: string, ...reads: readonly (readonly Meet[] | undefined)[]): Meet[] {
  const byID = new Map<string, Meet>();
  for (const read of reads) for (const m of read ?? []) if (Array.isArray(m.people) && m.people.includes(athleteID)) byID.set(m.id, m);
  return [...byID.values()].sort(byDate);
}

/** The next meet they are going to, one under way included. */
export function nextMeetFor(meets: readonly Meet[] | undefined, athleteID: string, today: string): Meet | undefined {
  return comingUp(meetsFor(athleteID, meets), today)[0];
}

export interface MeetFields {
  name: string;
  date: string;
  endDate: string;
  place: string;
}

export type MeetProblems = Partial<Record<keyof MeetFields, string>>;

export function meetProblems(f: MeetFields): MeetProblems {
  const out: MeetProblems = {};
  const name = f.name.trim();
  if (!name) out.name = "A meet needs a name.";
  else if (name.length > MEET_TEXT_MAX) out.name = `At most ${MEET_TEXT_MAX} characters.`;
  if (!isDay(f.date)) out.date = "Pick the day it starts.";
  if (f.endDate && !isDay(f.endDate)) out.endDate = "Pick the day it ends, or leave it empty.";
  else if (f.endDate && isDay(f.date) && f.endDate < f.date) out.endDate = "It ends on or after the day it starts.";
  if (f.place.trim().length > MEET_TEXT_MAX) out.place = `At most ${MEET_TEXT_MAX} characters.`;
  return out;
}

/** What is sent for the fields: trimmed, an end on the start day is no end, empty is none. */
export function meetBody(f: MeetFields): { name: string; date: string; endDate: string | null; place: string | null } {
  const place = f.place.trim();
  return { name: f.name.trim(), date: f.date, endDate: f.endDate && f.endDate > f.date ? f.endDate : null, place: place || null };
}

/** What was read with what this page wrote since over it (`null`: deleted), kept to those overlapping `from`..`until`. */
export function withWrites(read: readonly Meet[], writes: ReadonlyMap<string, Meet | null>, from: string, until: string): Meet[] {
  const out = read.filter((m) => !writes.has(m.id));
  for (const m of writes.values()) if (m && m.date <= until && lastDay(m) >= from) out.push(m);
  return out.sort(byDate);
}
